'use client'

import { Suspense, useEffect, useState } from 'react'
import Link from 'next/link'
import { signIn, useSession } from 'next-auth/react'
import { useRouter, useSearchParams } from 'next/navigation'
import { ArrowLeft, ChevronRight, Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react'
import { AuthShell, AuthCard, AuthField, AuthAlert, BrandBlock, OrDivider, authInputCls, primaryPillCls } from '@/components/compte/AuthUI'
import { isNativeApp } from '@/lib/native-app'

export default function LoginPage() {
  return (
    <Suspense fallback={null}>
      <LoginInner />
    </Suspense>
  )
}

function LoginInner() {
  const { status } = useSession()
  const router = useRouter()
  const params = useSearchParams()
  const callbackUrl = params.get('callbackUrl') || '/app'

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [hasGoogle, setHasGoogle] = useState(false)

  useEffect(() => {
    if (status === 'authenticated') router.replace(callbackUrl)
  }, [status, callbackUrl, router])

  // NextAuth redirige ici avec ?error=... quand le callback Google échoue
  // (ex. cookie state manquant) — jusqu'ici IGNORÉ → l'utilisateur ne voyait
  // rien se passer et pensait que « la connexion ne se fait pas ».
  useEffect(() => {
    const err = params.get('error')
    if (!err) return
    setError(
      err === 'OAuthCallback' || err === 'OAuthCallbackError'
        ? "La connexion Google a échoué en cours de route (souvent un souci de cookie bloqué par le navigateur). Réessaie, ou utilise la connexion par email."
        : err === 'AccessDenied'
          ? 'Connexion Google refusée.'
          : `Échec de connexion (${err}). Réessaie.`
    )
  }, [params])

  // Détecte si Google OAuth est configuré côté serveur → affiche le bouton.
  useEffect(() => {
    fetch('/api/auth/providers').then(r => r.json()).then(j => setHasGoogle(!!j?.google)).catch(() => {})
  }, [])

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true); setError(null)
    const r = await signIn('credentials', {
      email, password, redirect: false, callbackUrl,
    })
    setBusy(false)
    if (r?.error) {
      setError('Email ou mot de passe incorrect.')
    } else if (r?.ok) {
      router.replace(callbackUrl)
    }
  }

  // Google BLOQUE l'OAuth dans une WebView (APK TV/mobile). Dans ce cas on passe
  // par la connexion assistée par téléphone (QR → vrai navigateur → l'appli
  // récupère la session). Dans un vrai navigateur : OAuth Google direct.
  const googleLogin = () => {
    // isNativeApp() : window.__blcAndroid / classe .tv ne sont posés QUE côté
    // APK TV portrait forcé → toujours faux sur le téléphone standard, ce qui
    // envoyait ces utilisateurs droit dans l'OAuth Google DIRECT (bloqué en
    // WebView, échec silencieux) au lieu du relai par téléphone. Corrigé avec
    // une détection qui marche sur TOUS les flavors (bug probable derrière
    // « la connexion Google ne se fait pas » signalé côté appli téléphone).
    if (isNativeApp()) { window.dispatchEvent(new CustomEvent('blc-tv-login-open')); return }
    signIn('google', { callbackUrl })
  }

  return (
    <AuthShell
      top={
        <Link href="/" aria-label="Retour"
          className="absolute top-4 left-4 z-10 w-10 h-10 rounded-full grid place-items-center text-white/80 hover:text-white bg-white/[0.06] border border-white/10 hover:bg-white/[0.12] transition">
          <ArrowLeft size={18} />
        </Link>
      }>
      <div className="text-center mb-7">
        <BrandBlock size="lg" />
        <div className="text-[11px] font-semibold tracking-[0.28em] text-white/50 mt-7 mb-2">CONNEXION</div>
        <h1 className="text-[28px] md:text-[34px] font-extrabold tracking-tight leading-tight">Content de te revoir</h1>
        <p className="text-sm text-white/55 mt-2 max-w-xs mx-auto">
          Retrouve tes favoris, playlists et historique sur tous tes appareils.
        </p>
      </div>

      <AuthCard>
        {/* Connexion Google — apparaît dès que Google OAuth est configuré */}
        {hasGoogle && (
          <div className="mb-5">
            <button type="button"
              onClick={googleLogin}
              className="w-full h-12 rounded-full text-sm font-semibold inline-flex items-center justify-center gap-2.5 bg-white text-[#17171F] hover:bg-white/90 active:scale-[0.99] transition">
              <GoogleG /> Continuer avec Google
            </button>
            <p className="text-[11.5px] text-white/45 text-center mt-2.5">
              Synchronise favoris, playlists et historique sur tous tes appareils.
            </p>
            <div className="mt-5"><OrDivider>ou par email</OrDivider></div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-4">
          <AuthField icon={<Mail size={17} />} label="Email">
            <input type="email" required autoFocus
              value={email} onChange={e => setEmail(e.target.value)}
              autoComplete="email"
              placeholder="ton@email.com"
              className={authInputCls} />
          </AuthField>

          <AuthField icon={<Lock size={17} />} label="Mot de passe"
            right={
              <button type="button" onClick={() => setShowPwd(s => !s)}
                aria-label={showPwd ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                className="text-white/55 hover:text-white p-1 -mr-1">
                {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            }>
            <input type={showPwd ? 'text' : 'password'} required
              value={password} onChange={e => setPassword(e.target.value)}
              autoComplete="current-password" minLength={6}
              placeholder="••••••••"
              className={authInputCls} />
          </AuthField>

          {error && <AuthAlert>{error}</AuthAlert>}

          <button type="submit" disabled={busy} className={primaryPillCls}>
            {busy ? <Loader2 size={16} className="animate-spin" /> : null}
            Se connecter
          </button>

          <div className="text-center text-sm text-white/60 pt-4 border-t border-white/[0.07]">
            Pas encore de compte ?{' '}
            <Link href={`/register${callbackUrl !== '/app' ? `?callbackUrl=${encodeURIComponent(callbackUrl)}` : ''}`}
              className="text-a4 hover:text-white font-semibold transition">
              Créer un compte
            </Link>
          </div>
        </form>
      </AuthCard>

      <div className="text-center mt-6">
        <Link href="/app" className="inline-flex items-center gap-1 text-sm text-white/55 hover:text-white transition">
          Continuer sans compte <ChevronRight size={15} />
        </Link>
      </div>
    </AuthShell>
  )
}

function GoogleG() {
  return (
    <svg width="17" height="17" viewBox="0 0 48 48" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8a12 12 0 1 1 0-24c3.1 0 5.9 1.2 8 3.1l5.7-5.7A20 20 0 1 0 24 44c11 0 20-8 20-20 0-1.3-.1-2.3-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8A12 12 0 0 1 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7A20 20 0 0 0 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2A12 12 0 0 1 24 36c-5.2 0-9.6-3.3-11.2-8l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3a12 12 0 0 1-4.1 5.6l6.2 5.2C39.9 41.5 44 36.5 44 24c0-1.3-.1-2.3-.4-3.5z" />
    </svg>
  )
}
